import React from "react";
import { useAccessibility } from "@/context/AccessibilityContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Accessibility, Eye, Wind, RotateCcw } from "lucide-react";

export const AccessibilityToolbar = ({ triggerClassName = "", align = "end" }) => {
  const a = useAccessibility();
  if (!a) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="accessibility-toggle-button"
          aria-label="Accessibility options"
          className={`mmm-tap inline-flex items-center justify-center h-10 w-10 rounded-lg text-[#1C4358] hover:bg-[rgba(28,67,88,0.10)] transition-colors ${triggerClassName}`}
        >
          <Accessibility size={20} />
        </button>
      </PopoverTrigger>
      <PopoverContent align={align} className="w-80 p-4" data-testid="accessibility-panel">
        <div className="flex items-center gap-2 mb-3">
          <Accessibility size={18} className="text-[#9C1E22]" />
          <div className="font-display font-semibold text-[#1C4358]">Accessibility</div>
        </div>

        <div className="mb-4">
          <Label className="text-[13px] font-medium text-[#252221]">Text size</Label>
          <ToggleGroup
            type="single"
            value={a.textSize}
            onValueChange={(v) => v && a.setTextSize(v)}
            className="mt-2 grid grid-cols-3 gap-1.5"
          >
            <ToggleGroupItem value="standard" data-testid="text-size-standard" aria-label="Standard text size"
              className="mmm-tap data-[state=on]:bg-[#1C4358] data-[state=on]:text-white border border-[#E6EDF2]">
              <span className="text-sm font-semibold">A</span>
            </ToggleGroupItem>
            <ToggleGroupItem value="large" data-testid="text-size-large" aria-label="Large text size"
              className="mmm-tap data-[state=on]:bg-[#1C4358] data-[state=on]:text-white border border-[#E6EDF2]">
              <span className="text-base font-semibold">A+</span>
            </ToggleGroupItem>
            <ToggleGroupItem value="xl" data-testid="text-size-xl" aria-label="Extra large text size"
              className="mmm-tap data-[state=on]:bg-[#1C4358] data-[state=on]:text-white border border-[#E6EDF2]">
              <span className="text-lg font-semibold">A++</span>
            </ToggleGroupItem>
          </ToggleGroup>
          <p className="mt-1.5 text-xs text-muted-foreground">Standard · Large · Extra Large</p>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Eye size={16} className="text-[#1C4358]" />
              <Label htmlFor="a11y-hr" className="text-sm font-medium">High readability</Label>
            </div>
            <Switch id="a11y-hr" checked={a.highReadability} onCheckedChange={a.setHighReadability} data-testid="toggle-high-readability" />
          </div>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Wind size={16} className="text-[#1C4358]" />
              <Label htmlFor="a11y-rm" className="text-sm font-medium">Reduce motion</Label>
            </div>
            <Switch id="a11y-rm" checked={a.reducedMotion} onCheckedChange={a.setReducedMotion} data-testid="toggle-reduced-motion" />
          </div>
        </div>

        <button
          type="button"
          onClick={a.reset}
          data-testid="accessibility-reset"
          className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium text-[#1C4358] hover:underline"
        >
          <RotateCcw size={13} /> Reset to defaults
        </button>
      </PopoverContent>
    </Popover>
  );
};
